import { useState } from 'react';
import { VendorSetupFeatures } from '@/contracts/vendor-setup';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Plus, Trash2, Star, Sparkles, Shield, Zap } from 'lucide-react';

interface Props {
  data: VendorSetupFeatures;
  onChange: (data: VendorSetupFeatures) => void;
}

const ICON_OPTIONS = [
  { value: 'star', label: 'Star', icon: Star },
  { value: 'sparkles', label: 'Sparkles', icon: Sparkles },
  { value: 'shield', label: 'Shield', icon: Shield },
  { value: 'zap', label: 'Zap', icon: Zap },
];

export function SetupFeaturesStep({ data, onChange }: Props) {
  const addFeature = () => {
    onChange({ ...data, features: [...data.features, ''] });
  };

  const updateFeature = (index: number, value: string) => {
    const newFeatures = [...data.features];
    newFeatures[index] = value;
    onChange({ ...data, features: newFeatures });
  };

  const removeFeature = (index: number) => {
    const newFeatures = data.features.filter((_, i) => i !== index);
    onChange({ ...data, features: newFeatures });
  };

  const addHighlight = () => {
    onChange({
      ...data,
      feature_highlights: [
        ...data.feature_highlights,
        { icon: 'star', title: '', description: '' },
      ],
    });
  };

  const updateHighlight = (index: number, updates: Partial<typeof data.feature_highlights[0]>) => {
    const newHighlights = [...data.feature_highlights];
    newHighlights[index] = { ...newHighlights[index], ...updates };
    onChange({ ...data, feature_highlights: newHighlights });
  };

  const removeHighlight = (index: number) => {
    const newHighlights = data.feature_highlights.filter((_, i) => i !== index);
    onChange({ ...data, feature_highlights: newHighlights });
  };

  const updateAtAGlance = (key: string, value: string) => {
    onChange({
      ...data,
      at_a_glance: {
        ...data.at_a_glance,
        [key]: value,
      },
    });
  };

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-xl font-semibold mb-2">Features & Benefits</h2>
        <p className="text-muted-foreground">
          Highlight what makes your product special. Focus on benefits agents care about.
        </p>
      </div>

      {/* At a Glance */}
      <div className="space-y-4">
        <Label className="text-lg font-medium">At a Glance</Label>
        <p className="text-sm text-muted-foreground">
          Quick summary points that appear at the top of your listing
        </p>
        
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="summary">Summary</Label>
            <Textarea
              id="summary"
              value={data.at_a_glance?.summary || ''}
              onChange={(e) => updateAtAGlance('summary', e.target.value)}
              placeholder="A brief summary of your product in 1-2 sentences"
              rows={2}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="who_its_for">Who It's For</Label>
            <Input
              id="who_its_for"
              value={data.at_a_glance?.who_its_for || ''}
              onChange={(e) => updateAtAGlance('who_its_for', e.target.value)}
              placeholder="e.g., Solo agents doing 10+ deals/year who need lead automation"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="what_you_get">What You Get</Label>
            <Input
              id="what_you_get"
              value={data.at_a_glance?.what_you_get || ''}
              onChange={(e) => updateAtAGlance('what_you_get', e.target.value)}
              placeholder="e.g., Full CRM access, 1000 contacts, email automation, mobile app"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="guarantee">Guarantee</Label>
            <Input
              id="guarantee"
              value={data.at_a_glance?.guarantee || ''}
              onChange={(e) => updateAtAGlance('guarantee', e.target.value)}
              placeholder="e.g., 30-day money-back guarantee"
            />
          </div>
        </div>
      </div>

      {/* Feature List */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <Label className="text-lg font-medium">Key Features</Label>
            <p className="text-sm text-muted-foreground">List your main features (bullet points)</p>
          </div>
          <Button variant="outline" size="sm" onClick={addFeature}>
            <Plus className="h-4 w-4 mr-1" />
            Add Feature
          </Button>
        </div>

        <div className="space-y-2">
          {data.features.map((feature, index) => (
            <div key={index} className="flex gap-2">
              <Input
                value={feature}
                onChange={(e) => updateFeature(index, e.target.value)}
                placeholder="e.g., Automated follow-up sequences"
              />
              <Button
                variant="ghost"
                size="icon"
                onClick={() => removeFeature(index)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
          {data.features.length === 0 && (
            <p className="text-sm text-muted-foreground py-4 text-center border-2 border-dashed rounded-lg">
              No features added yet. Click "Add Feature" to get started.
            </p>
          )}
        </div>
      </div>

      {/* Feature Highlights */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <Label className="text-lg font-medium">Feature Highlights</Label>
            <p className="text-sm text-muted-foreground">Showcase 3-4 key benefits with icons</p>
          </div>
          <Button variant="outline" size="sm" onClick={addHighlight} disabled={data.feature_highlights.length >= 4}>
            <Plus className="h-4 w-4 mr-1" />
            Add Highlight
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {data.feature_highlights.map((highlight, index) => (
            <Card key={index}>
              <CardContent className="pt-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex gap-2">
                    {ICON_OPTIONS.map((opt) => {
                      const IconComponent = opt.icon;
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => updateHighlight(index, { icon: opt.value })}
                          className={`p-2 rounded-md ${
                            highlight.icon === opt.value 
                              ? 'bg-primary text-primary-foreground' 
                              : 'bg-muted hover:bg-muted/80'
                          }`}
                        >
                          <IconComponent className="h-4 w-4" />
                        </button>
                      );
                    })}
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => removeHighlight(index)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                <Input
                  value={highlight.title}
                  onChange={(e) => updateHighlight(index, { title: e.target.value })}
                  placeholder="Feature title"
                />
                <Textarea
                  value={highlight.description}
                  onChange={(e) => updateHighlight(index, { description: e.target.value })}
                  placeholder="Brief description of this feature"
                  rows={2}
                />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}
